import React, { createContext, useContext, useState, useMemo, useCallback } from "react";

const dict = {
  fr: {
    brand: "Impact Digital",
    productName: "Impact Audit AI",
    nav: {
      admin: "Espace Impact",
      startAudit: "Analyser mon entreprise",
      backToSite: "Retour au site principal",
    },
    lang: { fr: "FR", en: "EN", switchTo: "English" },
    landing: {
      badge: "Analyse préliminaire par IA",
      headline: "Découvrez le potentiel réel de votre acquisition numérique.",
      subheadline:
        "Obtenez une première analyse de votre site Web, de votre potentiel Google Ads et de votre parcours de conversion.",
      primaryCta: "Analyser mon entreprise",
      reassurance:
        "Analyse préliminaire gratuite • Aucune carte de crédit requise",
      sectionCapabilities: "Ce que nous analysons",
      cap1Title: "Site & technique",
      cap1Body:
        "HTTPS, balises, structure, viewport mobile, temps de chargement et attributs des images.",
      cap2Title: "Conversion",
      cap2Body:
        "CTA, formulaires, page de contact, témoignages, avis et certifications repérables.",
      cap3Title: "Préparation acquisition payante",
      cap3Body:
        "Chemin de conversion, tracking détectable publiquement et éléments de confiance — sans accès à votre compte publicitaire.",
      cap4Title: "Présence locale",
      cap4Body:
        "Téléphone, courriel, adresse postale et réseaux sociaux détectables sur vos pages publiques.",
      trustTitle: "Pensé pour les entreprises canadiennes en croissance",
      trustBody:
        "Impact Digital accompagne des entreprises qui veulent transformer leur site web en véritable moteur d'acquisition.",
    },
    audit: {
      progress: "Étape",
      of: "sur",
      next: "Continuer",
      back: "Retour",
      submit: "Lancer l'analyse",
      required: "Champ requis",
      invalidEmail: "Adresse courriel invalide",
      invalidUrl: "URL invalide",
      steps: {
        company: "Entreprise",
        objectives: "Objectifs",
        ads: "Google Ads",
        contact: "Contact",
      },
      step1: {
        title: "Parlez-nous de votre entreprise",
        subtitle: "Ces informations nous permettent de contextualiser l'analyse.",
        companyName: "Nom de l'entreprise",
        websiteUrl: "URL du site Web",
        industry: "Secteur d'activité",
        industryPlaceholder: "Sélectionnez un secteur",
        city: "Ville / zone desservie",
      },
      step2: {
        title: "Quel est votre objectif principal?",
        subtitle: "Choisissez celui qui reflète le mieux votre priorité actuelle.",
        options: {
          leads: "Générer plus de prospects",
          calls: "Générer des appels téléphoniques",
          sales: "Augmenter les ventes en ligne",
          appointments: "Augmenter les prises de rendez-vous",
          ads: "Améliorer la performance de mes Google Ads",
          web: "Améliorer mon site Web",
          unsure: "Je ne suis pas encore certain",
        },
      },
      step3: {
        title: "Diffusez-vous actuellement des Google Ads?",
        subtitle: "Aucune donnée privée n'est requise pour cette analyse préliminaire.",
        options: {
          yes: "Oui",
          no: "Non",
          previously: "Déjà par le passé",
          unknown: "Je ne sais pas",
        },
        spendTitle: "Budget mensuel approximatif",
        spend: [
          "Moins de 1 000 $",
          "1 000 $ – 2 500 $",
          "2 500 $ – 5 000 $",
          "5 000 $ – 10 000 $",
          "10 000 $ et plus",
          "Je préfère ne pas répondre",
        ],
      },
      step4: {
        title: "Où recevoir votre analyse?",
        subtitle:
          "Nous utilisons ces coordonnées uniquement pour vous transmettre votre analyse et, si utile, discuter de vos priorités.",
        firstName: "Prénom",
        lastName: "Nom",
        email: "Courriel professionnel",
        phone: "Téléphone",
        consent:
          "J'accepte d'être contacté par Impact Digital au sujet de cette analyse. Aucun engagement.",
      },
    },
    analysis: {
      steps: [
        "Crawl multi-pages de votre site…",
        "Extraction des faits vérifiables…",
        "Évaluation de la préparation acquisition payante…",
        "Synthèse par catégorie et opportunités prioritaires…",
      ],
      hint: "L'analyse peut prendre jusqu'à 40 secondes.",
    },
    results: {
      badge: "Analyse préliminaire",
      execSummary: "Résumé exécutif",
      catTitle: "Scores par catégorie",
      opportunitiesTitle: "Trois priorités identifiées",
      recTitle: "Recommandation Impact Digital",
      factsTitle: "Faits vérifiés",
      cta1: "Réserver une consultation stratégique",
      cta2: "Recevoir mon rapport par courriel",
      finalTitle: "Vous voulez transformer ces opportunités en plan d'action?",
      finalBody:
        "Nos équipes peuvent bâtir avec vous un plan d'acquisition axé sur la performance.",
      priority: {
        low: "Priorité faible",
        medium: "Priorité moyenne",
        high: "Priorité élevée",
      },
      dimensions: {
        site_technical: "Site & technique",
        conversion: "Conversion",
        local_presence: "Présence locale",
        paid_acquisition_readiness: "Préparation acquisition payante",
      },
      services: {
        google_ads_management: "Gestion de campagnes Google Ads",
        google_ads_optimization: "Optimisation Google Ads",
        conversion_landing_page: "Page de destination axée conversion",
        website_redesign: "Refonte de site Web",
        website_conversion_optimization: "Optimisation de la conversion",
      },
      status: {
        VERIFIED: "Vérifié",
        NOT_DETECTED: "Non détecté",
        UNKNOWN: "À confirmer",
      },
      seeEvidence: "Voir la preuve",
      hideEvidence: "Masquer",
      source: "Source",
      coverage: "Couverture de l'analyse",
      coverageTemplate: "{checked} critères vérifiés sur {total} critères applicables",
      noScoreYet: "Score indisponible (aucun critère mesurable pour cette catégorie).",
      strengths: "Points forts",
      opportunities: "Opportunités",
      basedOn: "Basé sur",
      pagesAnalyzed: "pages analysées",
      unreachableBanner:
        "Le site n'a pas pu être atteint. Tous les critères sont marqués « à confirmer ».",
      paidNotice:
        "Cette section évalue uniquement la préparation du site à recevoir du trafic payant, sur la base de signaux publiquement observables. La performance réelle d'un compte Google Ads (CPC, CPA, conversions, budget) n'est pas vérifiable publiquement et n'est pas mesurée ici.",
      disclaimer:
        "Analyse préliminaire basée sur des signaux publiquement observables. Aucun résultat publicitaire n'est garanti.",
      emailSent: "Rapport envoyé par courriel avec succès.",
      factLabels: {
        https: "HTTPS",
        title: "Balise <title>",
        meta_description: "Meta description",
        viewport_meta: "Meta viewport (mobile)",
        favicon: "Favicon",
        h1: "Titre H1",
        multiple_h1_detected: "Plusieurs H1 sur la page d'accueil",
        server_response_observation: "Observation du temps de réponse serveur",
        images_alt: "Attributs alt des images",
        structured_data: "Données structurées JSON-LD",
        ctas: "Appels à l'action détectés",
        forms: "Formulaires de captation de prospects détectés",
        contact_page: "Page contact",
        testimonials: "Témoignages",
        reviews: "Avis / évaluations",
        certifications: "Certifications",
        partners_associations: "Partenaires / associations",
        phone: "Téléphone",
        email: "Courriel",
        address: "Adresse postale",
        social_links: "Liens réseaux sociaux",
        dedicated_service_page_detected: "Page de service dédiée détectée",
        mobile_ready: "Préparation mobile (viewport)",
        cta_presence: "Présence de CTA",
        contact_path: "Chemin de contact clair",
        tracking_public: "Outils de suivi publiquement détectables",
        trust_elements: "Éléments de confiance (agrégé)",
      },
      scoredCriteria: "Critères pris en compte dans le score",
      displayOnly: "Affiché à titre indicatif — n'influence pas le score",
    },
    admin: {
      loginTitle: "Espace Impact Digital",
      loginSubtitle: "Tableau de bord des audits",
      email: "Courriel",
      password: "Mot de passe",
      signIn: "Se connecter",
      invalid: "Identifiants invalides",
      dashboardTitle: "Tableau de bord",
      signOut: "Se déconnecter",
      kpi: {
        total: "Audits totaux",
        new: "Nouveaux (7 jours)",
        avg: "Score moyen",
        hot: "Prospects prioritaires",
        gaOpp: "Opps. acquisition payante",
        webOpp: "Opps. Site Web",
      },
      table: {
        date: "Date",
        company: "Entreprise",
        contact: "Contact",
        city: "Ville",
        objective: "Objectif",
        ads: "Google Ads",
        score: "Score moyen",
        lead: "Lead",
        services: "Services recommandés",
        actions: "Détails",
      },
      search: "Rechercher (entreprise, courriel, ville)",
      quality: {
        all: "Tous les leads",
        hot: "Prioritaires",
        warm: "Chauds",
        cold: "Froids",
      },
      empty: "Aucun audit pour le moment.",
      viewDetails: "Voir les détails",
    },
    adminModal: {
      title: "Détails de l'audit",
      loading: "Chargement des faits vérifiés…",
      industry: "Secteur",
      city: "Ville",
      createdAt: "Créé le",
      objective: "Objectif",
      contact: "Contact",
      phone: "Téléphone",
      ads: "Google Ads",
      unknownShort: "à confirmer",
      crawl: "Crawl et métadonnées",
      homeUrl: "URL analysée",
      reachable: "Site atteignable",
      pagesAnalyzed: "Pages analysées",
      leadScore: "Score lead interne",
      hubspot: "HubSpot",
      confMedium: "conf. moyenne",
      confLow: "conf. faible",
      errorNotFound: "Audit introuvable.",
      errorGeneric: "Impossible de charger cet audit.",
      retry: "Réessayer",
    },
  },
  en: {
    brand: "Impact Digital",
    productName: "Impact Audit AI",
    nav: {
      admin: "Impact team",
      startAudit: "Analyze my business",
      backToSite: "Back to main website",
    },
    lang: { fr: "FR", en: "EN", switchTo: "Français" },
    landing: {
      badge: "AI-powered preliminary analysis",
      headline: "Discover the true potential of your digital acquisition.",
      subheadline:
        "Get a first-look analysis of your website, Google Ads potential and conversion journey.",
      primaryCta: "Analyze my business",
      reassurance: "Free preliminary analysis • No credit card required",
      sectionCapabilities: "What we analyze",
      cap1Title: "Site & technical",
      cap1Body:
        "HTTPS, tags, structure, mobile viewport, load time and image attributes.",
      cap2Title: "Conversion",
      cap2Body:
        "CTAs, forms, contact page, testimonials, reviews and certifications detectable on public pages.",
      cap3Title: "Paid acquisition readiness",
      cap3Body:
        "Conversion path, publicly detectable tracking and trust elements — without accessing your ad account.",
      cap4Title: "Local presence",
      cap4Body:
        "Phone, email, mailing address and social links detectable on your public pages.",
      trustTitle: "Built for growing Canadian businesses",
      trustBody:
        "Impact Digital helps businesses turn their website into a real acquisition engine.",
    },
    audit: {
      progress: "Step",
      of: "of",
      next: "Continue",
      back: "Back",
      submit: "Run analysis",
      required: "Required field",
      invalidEmail: "Invalid email address",
      invalidUrl: "Invalid URL",
      steps: {
        company: "Company",
        objectives: "Objectives",
        ads: "Google Ads",
        contact: "Contact",
      },
      step1: {
        title: "Tell us about your business",
        subtitle: "This helps us contextualize the analysis.",
        companyName: "Company name",
        websiteUrl: "Website URL",
        industry: "Industry",
        industryPlaceholder: "Select an industry",
        city: "City / service area",
      },
      step2: {
        title: "What is your primary objective?",
        subtitle: "Pick the one that best reflects your current priority.",
        options: {
          leads: "Generate more leads",
          calls: "Generate phone calls",
          sales: "Increase online sales",
          appointments: "Increase appointment bookings",
          ads: "Improve Google Ads performance",
          web: "Improve my website",
          unsure: "I'm not sure yet",
        },
      },
      step3: {
        title: "Are you currently running Google Ads?",
        subtitle: "No private data is required for this preliminary analysis.",
        options: {
          yes: "Yes",
          no: "No",
          previously: "Previously",
          unknown: "I don't know",
        },
        spendTitle: "Approximate monthly ad spend",
        spend: [
          "Less than $1,000",
          "$1,000 – $2,500",
          "$2,500 – $5,000",
          "$5,000 – $10,000",
          "$10,000+",
          "Prefer not to say",
        ],
      },
      step4: {
        title: "Where should we send your analysis?",
        subtitle:
          "We use this only to send you your analysis and, if useful, discuss your priorities.",
        firstName: "First name",
        lastName: "Last name",
        email: "Professional email",
        phone: "Phone",
        consent:
          "I agree to be contacted by Impact Digital about this analysis. No commitment.",
      },
    },
    analysis: {
      steps: [
        "Multi-page crawl of your site…",
        "Extracting verifiable facts…",
        "Evaluating paid acquisition readiness…",
        "Synthesizing categories and top opportunities…",
      ],
      hint: "The analysis can take up to 40 seconds.",
    },
    results: {
      badge: "Preliminary analysis",
      execSummary: "Executive summary",
      catTitle: "Scores by category",
      opportunitiesTitle: "Three identified priorities",
      recTitle: "Impact Digital recommendation",
      factsTitle: "Verified facts",
      cta1: "Book a strategy call",
      cta2: "Email me my report",
      finalTitle: "Want to turn these opportunities into an action plan?",
      finalBody:
        "Our teams can help you build a performance-focused acquisition plan.",
      priority: {
        low: "Low priority",
        medium: "Medium priority",
        high: "High priority",
      },
      dimensions: {
        site_technical: "Site & technical",
        conversion: "Conversion",
        local_presence: "Local presence",
        paid_acquisition_readiness: "Paid acquisition readiness",
      },
      services: {
        google_ads_management: "Google Ads management",
        google_ads_optimization: "Google Ads optimization",
        conversion_landing_page: "Conversion landing page",
        website_redesign: "Website redesign",
        website_conversion_optimization: "Website conversion optimization",
      },
      status: {
        VERIFIED: "Verified",
        NOT_DETECTED: "Not detected",
        UNKNOWN: "To confirm",
      },
      seeEvidence: "See evidence",
      hideEvidence: "Hide",
      source: "Source",
      coverage: "Coverage of the analysis",
      coverageTemplate: "{checked} verified criteria out of {total} applicable",
      noScoreYet: "Score unavailable (no measurable criteria for this category).",
      strengths: "Strengths",
      opportunities: "Opportunities",
      basedOn: "Based on",
      pagesAnalyzed: "pages analyzed",
      unreachableBanner:
        "The website could not be reached. All criteria are marked \"to confirm\".",
      paidNotice:
        "This section only evaluates the site's readiness to receive paid traffic, based on publicly observable signals. Real Google Ads account performance (CPC, CPA, conversions, spend) is not publicly verifiable and is not measured here.",
      disclaimer:
        "Preliminary analysis based on publicly observable signals. No advertising results are guaranteed.",
      emailSent: "Report emailed successfully.",
      factLabels: {
        https: "HTTPS",
        title: "<title> tag",
        meta_description: "Meta description",
        viewport_meta: "Meta viewport (mobile)",
        favicon: "Favicon",
        h1: "H1 heading",
        multiple_h1_detected: "Multiple H1s on the home page",
        server_response_observation: "Server response time observation",
        images_alt: "Image alt attributes",
        structured_data: "JSON-LD structured data",
        ctas: "Detected calls to action",
        forms: "Lead capture forms detected",
        contact_page: "Contact page",
        testimonials: "Testimonials",
        reviews: "Reviews / ratings",
        certifications: "Certifications",
        partners_associations: "Partners / associations",
        phone: "Phone",
        email: "Email",
        address: "Mailing address",
        social_links: "Social media links",
        dedicated_service_page_detected: "Dedicated service page detected",
        mobile_ready: "Mobile readiness (viewport)",
        cta_presence: "CTA presence",
        contact_path: "Clear contact path",
        tracking_public: "Publicly detectable tracking tools",
        trust_elements: "Trust elements (aggregate)",
      },
      scoredCriteria: "Criteria included in the score",
      displayOnly: "Displayed for information — does not affect the score",
    },
    admin: {
      loginTitle: "Impact Digital admin",
      loginSubtitle: "Audit dashboard",
      email: "Email",
      password: "Password",
      signIn: "Sign in",
      invalid: "Invalid credentials",
      dashboardTitle: "Dashboard",
      signOut: "Sign out",
      kpi: {
        total: "Total audits",
        new: "New (7 days)",
        avg: "Average score",
        hot: "High-potential leads",
        gaOpp: "Paid-acq. opportunities",
        webOpp: "Website opportunities",
      },
      table: {
        date: "Date",
        company: "Company",
        contact: "Contact",
        city: "City",
        objective: "Objective",
        ads: "Google Ads",
        score: "Avg score",
        lead: "Lead",
        services: "Recommended services",
        actions: "Details",
      },
      search: "Search (company, email, city)",
      quality: {
        all: "All leads",
        hot: "High-potential",
        warm: "Warm",
        cold: "Cold",
      },
      empty: "No audits yet.",
      viewDetails: "View details",
    },
    adminModal: {
      title: "Audit details",
      loading: "Loading verified facts…",
      industry: "Industry",
      city: "City",
      createdAt: "Created at",
      objective: "Objective",
      contact: "Contact",
      phone: "Phone",
      ads: "Google Ads",
      unknownShort: "to confirm",
      crawl: "Crawl & metadata",
      homeUrl: "Analyzed URL",
      reachable: "Site reachable",
      pagesAnalyzed: "Pages analyzed",
      leadScore: "Internal lead score",
      hubspot: "HubSpot",
      confMedium: "medium conf.",
      confLow: "low conf.",
      errorNotFound: "Audit not found.",
      errorGeneric: "Could not load this audit.",
      retry: "Retry",
    },
  },
};

const I18nContext = createContext({ lang: "fr", t: (k) => k, setLang: () => {} });

export const I18nProvider = ({ children }) => {
  const [lang, setLang] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("impact_lang") || "fr";
    }
    return "fr";
  });

  const changeLang = useCallback((next) => {
    setLang(next);
    if (typeof window !== "undefined") localStorage.setItem("impact_lang", next);
  }, []);

  const t = useCallback(
    (key) => {
      const path = key.split(".");
      let cur = dict[lang];
      for (const p of path) {
        if (cur && typeof cur === "object" && p in cur) cur = cur[p];
        else return key;
      }
      return cur;
    },
    [lang]
  );

  const value = useMemo(() => ({ lang, t, setLang: changeLang }), [lang, t, changeLang]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export const useI18n = () => useContext(I18nContext);
